import React, { PropsWithChildren, useContext } from 'react'
import { Navigate } from 'react-router'
import Navigation from '@App/navigation'
import { useFlag, useOpenFeatureClientStatus } from '@openfeature/react-sdk'
import { ProviderStatus } from '@openfeature/web-sdk'
import RootStore from '@App/stores/root-store'
import { StoresContext } from '@App/contexts'
import { observer } from 'mobx-react'

export interface RequireFeatureFlagProps {
    flagKey: string
    withRedirect?: boolean
    reRouteLocation?: string
}

const RequireFeatureFlagInternal = ({
    flagKey,
    children,
    withRedirect,
    reRouteLocation,
}: PropsWithChildren<RequireFeatureFlagProps>) => {
    const { value: showFeature } = useFlag(flagKey, false)
    const providerStatus = useOpenFeatureClientStatus()
    const redirectLocation = reRouteLocation ?? (withRedirect ? Navigation.login.root() : undefined)
    // the provider resolves flags asynchronously, so redirecting before it settles would bounce users off an enabled feature
    const isFlagResolved = providerStatus !== ProviderStatus.NOT_READY

    if (showFeature) {
        return <>{children}</>
    } else if (redirectLocation && isFlagResolved) {
        return <Navigate to={redirectLocation} replace />
    } else {
        return <></>
    }
}

const RequireFeatureFlag = ({
    flagKey,
    children,
    withRedirect,
    reRouteLocation,
}: PropsWithChildren<RequireFeatureFlagProps>) => {
    const { authStore } = useContext<RootStore>(StoresContext)
    if (!authStore.isLoggedIn) {
        return null
    }
    return (
        <RequireFeatureFlagInternal flagKey={flagKey} withRedirect={withRedirect} reRouteLocation={reRouteLocation}>
            {children}
        </RequireFeatureFlagInternal>
    )
}

export default observer(RequireFeatureFlag)
